import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { Item } from "@volca/ui/components/ui/item";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { dayjs } from "@/core/utils/date";
import { packChatPreview } from "@/payroll/pack-chat/message-preview";
import {
  PACK_CHAT_MARK_MODE,
  PackChatStatusMark,
} from "@/payroll/pack-chat/pack-chat-status-mark";
import { referenceParts } from "@/payroll/pack-chat/reference-label";
import type { IPackChatThreadSummary } from "@/payroll/pack-chat/types";

export const PackChatThreadHeader = ({
  thread,
  expanded,
  onToggle,
}: {
  thread: IPackChatThreadSummary;
  expanded: boolean;
  onToggle: () => void;
}) => {
  const { path, label, value, valueIsFigure } = referenceParts(thread.context);
  const open = thread.status === "open";
  const count = thread.message_count;
  const last = thread.last_message;
  const preview = last ? packChatPreview(last) : null;
  const figure = value && valueIsFigure ? value : undefined;
  const messageCount = count
    ? `${count} message${count === 1 ? "" : "s"}`
    : null;
  const when = dayjs(thread.updated_at).fromNow();
  const meta = [path, messageCount, when].filter(Boolean).join(" · ");
  const phoneMeta = [figure, path, when].filter(Boolean).join(" · ");

  return (
    <Item
      render={<button type="button" />}
      aria-expanded={expanded}
      onClick={onToggle}
      className="rounded-none px-4 py-3 text-left"
    >
      <Stack
        className="w-full"
        direction="row"
        align="start"
        justify="between"
        gap={4}
      >
        <Stack gap={0} align="stretch" className="min-w-0">
          <Text
            variant="label"
            title={label}
            className={cn("truncate", {
              "text-subtle": !open,
            })}
          >
            {label}
          </Text>
          <Text variant="body" tone="muted" className="truncate">
            <span className="sm:hidden" title={phoneMeta}>
              {phoneMeta}
            </span>
            <span className="hidden sm:inline" title={meta}>
              {meta}
            </span>
          </Text>
          {!expanded && last && (
            <Text
              variant="body"
              tone="muted"
              title={
                preview
                  ? preview.author
                    ? `${preview.author}: ${preview.body}`
                    : preview.body
                  : undefined
              }
              className="truncate pt-0.5"
            >
              {preview &&
                (preview.author
                  ? `${preview.author}: ${preview.body}`
                  : preview.body)}
            </Text>
          )}
        </Stack>

        <Stack
          direction="row"
          align="center"
          gap={2}
          className="shrink-0 pt-0.5"
        >
          {figure && (
            <Text variant="label" className="hidden sm:block">
              {figure}
            </Text>
          )}
          <PackChatStatusMark
            focusable={false}
            hasAiMessages={thread.has_ai_messages}
            needsManager={thread.needs_manager}
            responseOwner={thread.response_owner}
            mode={PACK_CHAT_MARK_MODE.owner}
            open={open}
            label={open ? "Open" : "Closed"}
          />
          <CaretDownIcon
            className={cn("size-4 text-subtle transition-transform", {
              "rotate-180": expanded,
            })}
            aria-hidden
          />
        </Stack>
      </Stack>
    </Item>
  );
};
